import { useCallback, useEffect, useRef } from 'react';
import { styled } from 'goober';

import { ContactCardSkeleton } from '@/features/contacts/card/contact-card-skeleton';
import { ContactsFormContainer } from '@/features/contacts/contacts-form-container';
import { modalState } from '@/state/modal.state';
import { Stack } from '@/ui/line/line';
import type { IPageState } from '@/ui/table/table.types';
import type { CompanyContact } from '@/api/v4/organization-contacts.api';
import type { PrimitiveAtom } from 'jotai';
import { useAtomValue, useSetAtom } from 'jotai';

type ContactsInfiniteScrollProps = {
  formContactsState: PrimitiveAtom<CompanyContact[]>;
  visibleContacts?: CompanyContact[];
  addContactCounterState: PrimitiveAtom<number>;
  contactsEditModeState: PrimitiveAtom<boolean>;
  contactsSubmitFormState: PrimitiveAtom<boolean>;
  contactDeletedIdState: PrimitiveAtom<string | null>;
  contactsPageState: PrimitiveAtom<IPageState>;
  onFormSubmit: (contacts: CompanyContact[]) => void;
  deleteAction: (contactId: string, synced: boolean) => void;
  hasNextPage?: boolean;
  isLoading?: boolean;
};

export const ContactsInfiniteScroll = ({
  formContactsState,
  visibleContacts,
  addContactCounterState,
  contactsEditModeState,
  contactDeletedIdState,
  contactsSubmitFormState,
  contactsPageState,
  onFormSubmit,
  hasNextPage,
  isLoading,
  deleteAction,
}: ContactsInfiniteScrollProps) => {
  const loaderRef = useRef<HTMLDivElement>(null);

  const contactsEditMode = useAtomValue(contactsEditModeState);
  const forceSubmit = useAtomValue(contactsSubmitFormState);
  const addContactCounter = useAtomValue(addContactCounterState);
  const setModal = useSetAtom(modalState);
  const setPage = useSetAtom(contactsPageState);

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) {
          return;
        }

        if (!isLoading && hasNextPage) {
          setPage(({ page, ...rest }) => ({ ...rest, page: page + 1 }));
        }
      });
    },
    [hasNextPage, isLoading, setPage],
  );

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '50%',
      threshold: 0,
    };

    const observer = new IntersectionObserver(handleObserver, options);
    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [handleObserver]);

  const onDeleteHandler = useCallback(
    (contactId: string, synced: boolean) => {
      setModal({
        state: 'confirmDelete',
        onConfirm: () => deleteAction(contactId, synced),
      });
    },
    [deleteAction, setModal],
  );

  return (
    <Stack gap="0">
      <Grid>
        <ContactsFormContainer
          editMode={contactsEditMode}
          forceSubmit={forceSubmit}
          addContactCounter={addContactCounter}
          onSubmit={onFormSubmit}
          editState={contactsEditModeState}
          onDelete={onDeleteHandler}
          formContactsState={formContactsState}
          visibleContacts={visibleContacts}
          onContactDeletedState={contactDeletedIdState}
        />
        {isLoading && <ContactCardSkeleton />}
      </Grid>
      <span ref={loaderRef} />
    </Stack>
  );
};

const Grid = styled('div')`
  display: grid;
  grid-gap: 55px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  grid-template-rows: auto;
`;
